import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import classNames from '../../node_modules/classnames/index.js';
import { ExclamationCircle, InfoCircle, CheckCircle, Warning, Close } from '@livingdesign/icons';
import { componentAlertIconIconSize } from '@livingdesign/tokens/dist/js/light/regular/components/Alert';
import styles from './Alert.module.scss.js';
import { AlertActionButton } from './AlertActionButton.js';
import { applyCommonProps } from '../utility/commonProps.js';
import '@walmart-web/livingdesign-components/index.esm.css';
import { VisuallyHidden } from '@walmart-web/livingdesign-components';
import '../Button/Button.js';
import '../Button/ButtonGroup.js';
import '../Radio/Radio.js';
import '../Tag/Tag.js';
import '../Checkbox/Checkbox.js';
import '../Chip/Chip.js';
import '../IconButton/IconButton.js';
import '@livingdesign/tokens/dist/js/light/regular/components/Form';
import '../TextField/TextField.js';
import '../TabNavigation/TabNavigation.js';
import '../TabNavigation/TabNavigationItem.js';
import '../Modal/Modal.js';
import '@livingdesign/tokens/dist/js/light/regular/components/ErrorMessage';
import '../SearchBox/SearchBox.js';
import '../AutoComplete/AutoComplete.js';
import '../TextArea/TextArea.js';
import '../ProgressIndicator/ProgressIndicator.js';
import '../Panel/Panel.js';
import '../Calendar/locale/wmLocale.js';
import '../Calendar/locale/stdLocale.js';
import '../Calendar/context.js';
import 'dayjs';
import '../Calendar/RenderDays.js';
import '../Calendar/RenderWeeks.js';
import '../utility/constants.js';
import '@react-aria/ssr';
import '../CardActionArea/CardActionArea.js';
import '../HierarchySelector/HierarchySelector.js';
import '../Pagination/Pagination.js';
import '../DisplayTag/DisplayTag.js';
import '../PageHeader/PageHeader.js';
import '../Table/Table.js';
import '../Tooltip/Tooltip.js';
import '../ValueChange/ValueChange.js';

var _excluded = ["a11yIconLabel", "actionButtonProps", "children", "className", "variant", "actionLinkPosition", "closeButton", "onClose"];
/**
 * Alerts provide brief information and feedback to a user.
 *
 * - {@link https://livingdesign.walmart.com/components/alert/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/alert/react/ React documentation}
 *
 * ### Content
 *
 * #### Action button
 *
 * The Alert has an optional action button that allows users to interact. Set the `actionButtonProps` prop to enable the action button and it also has an option to customize the poistion of this action button using actionLinkPosition prop. There are three optins for actionLinkPosition: 'default', 'floating' and 'wrap':
 *
 * ```tsx
 * <Alert
 *   actionButtonProps={{
 *     children: "Action button",
 *     onClick(event) {
 *       console.log(event);
 *     }
 *   }}
 *   actionLinkPosition='floating'
 * >
 *   Alert
 * </Alert>
 * ```
 *
 * You can also change the action button to an `HTMLAnchorElement` by setting `href`:
 *
 * ```tsx
 * <Alert
 *   actionButtonProps={{
 *     children: "Go to account",
 *     href: "https://www.walmart.com/account",
 *     target: "_blank",
 *   }}
 * >
 *   Alert
 * </Alert>
 * ```
 *
 * ### Features
 *
 * #### Variant
 *
 * The Alert comes with four variants:
 *
 * - `error`: Conveys a negative message to the user.
 * - `info`: Provides general information in a highlighted way.
 * - `success`: Conveys a positive message to the user.
 * - `warning`: Draws the users attention to something that may affect their experience and maybe require their attention.
 *
 * Set the Alert's variant with the `variant` prop:
 *
 * ```tsx
 * <Alert variant="error">Error</Alert>
 * <Alert variant="info">Info</Alert>
 * <Alert variant="success">Success</Alert>
 * <Alert variant="warning">Warning</Alert>
 * ```
 *
 * #### Close Button
 *
 * The Alert comes with an option of close button. Set the value of closeButton to toggle the close button.
 *
 * ```tsx
 * <Alert closeButton={true}>Enable Close Button</Alert>
 * <Alert closeButton={false}>Disable Close Button</Alert>
 * ```
 *
 * ### Accessibility
 *
 * The variant is used as an a11y label on the Alert's leading icon. This label can be overwritten using the `a11yIconLabel` prop:
 *
 * ```tsx
 * <Alert variant="error" a11yIconLabel="problem">Error</Alert>
 * ```
 */
var Alert = function Alert(props) {
  var _applyCommonProps = applyCommonProps(props, Alert),
    a11yIconLabel = _applyCommonProps.a11yIconLabel,
    actionButtonProps = _applyCommonProps.actionButtonProps,
    children = _applyCommonProps.children,
    className = _applyCommonProps.className,
    _applyCommonProps$var = _applyCommonProps.variant,
    variant = _applyCommonProps$var === void 0 ? 'info' : _applyCommonProps$var,
    _applyCommonProps$act = _applyCommonProps.actionLinkPosition,
    actionLinkPosition = _applyCommonProps$act === void 0 ? 'default' : _applyCommonProps$act,
    _applyCommonProps$clo = _applyCommonProps.closeButton,
    closeButton = _applyCommonProps$clo === void 0 ? true : _applyCommonProps$clo,
    onClose = _applyCommonProps.onClose,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: classNames(styles.alert, variant === 'error' && styles.error, variant === 'info' && styles.info, variant === 'success' && styles.success, variant === 'warning' && styles.warning, className)
  }, rest), /*#__PURE__*/React.createElement("span", {
    className: styles.leading
  }, /*#__PURE__*/React.createElement(VisuallyHidden, null, "".concat(a11yIconLabel !== null && a11yIconLabel !== void 0 ? a11yIconLabel : variant, ":")), variant === 'error' && /*#__PURE__*/React.createElement(ExclamationCircle, {
    size: componentAlertIconIconSize
  }), variant === 'info' && /*#__PURE__*/React.createElement(InfoCircle, {
    size: componentAlertIconIconSize
  }), variant === 'success' && /*#__PURE__*/React.createElement(CheckCircle, {
    size: componentAlertIconIconSize
  }), variant === 'warning' && /*#__PURE__*/React.createElement(Warning, {
    size: componentAlertIconIconSize
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": "actionlink",
    className: classNames(actionLinkPosition === 'default' && styles.defaultaction, actionLinkPosition === 'floating' && styles.floating, actionLinkPosition === 'wrap' && styles.wrap)
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.alertmessage)
  }, children), /*#__PURE__*/React.createElement("div", {
    className: styles.actionlink
  }, actionButtonProps && /*#__PURE__*/React.createElement(AlertActionButton, actionButtonProps)))), closeButton ? /*#__PURE__*/React.createElement("div", {
    className: styles.closecontainer
  }, /*#__PURE__*/React.createElement("button", {
    "aria-label": "Close alert",
    "data-testid": "alert-close-button",
    className: classNames(styles.close, variant === 'error' && styles.error, variant === 'info' && styles.info, variant === 'success' && styles.success, variant === 'warning' && styles.warning),
    onClick: onClose
  }, /*#__PURE__*/React.createElement(Close, null))) : null);
};
Alert.displayName = 'Alert';

export { Alert };
